import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  DndContext, DragEndEvent, KeyboardSensor, PointerSensor, closestCenter, useSensor, useSensors,
} from '@dnd-kit/core';
import {
  SortableContext, arrayMove, sortableKeyboardCoordinates, useSortable, verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { Box, Button, Chip, IconButton, Paper, Tooltip, Typography } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import EditIcon from '@mui/icons-material/Edit';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import dayjs from 'dayjs';
import { useDataProvider, useInput } from 'react-admin';
import { TagType } from '../../../models';
import { __t } from '../../../i18n/t';
import BannerFormDialog from './BannerFormDialog';
import {
  CarouselCard, ChainModules, HomeBlock, HomeNavigation, ROW_ART, ShowWindowStatus,
  findNavigationChoice, navigationKeyOf, navigationTargetIdOf, newId, showWindowStatus,
} from './homeItems';

interface CarouselCardsInputProps {
  source?: string;
  chain?: ChainModules | null;
}

interface Named { id: string; name: string }

const ART = ROW_ART.full;
const THUMB_WIDTH = 132;

const DRAG_HANDLE_SX = {
  display: 'flex', alignItems: 'center', cursor: 'grab',
  color: 'text.disabled', touchAction: 'none',
};

const THUMB_SX = {
  width: THUMB_WIDTH, flexShrink: 0, aspectRatio: String(ART.ratio), borderRadius: 1,
  overflow: 'hidden', bgcolor: 'action.hover', display: 'flex', alignItems: 'center',
  justifyContent: 'center',
};

// Içados: ficam dentro de ternário no JSX, onde o plugin de i18n embrulharia os valores.
const THUMB_IMG_SX = { width: '100%', height: '100%', objectFit: 'cover' };

const MISSING_IMAGE_SX = {
  display: 'flex', flexDirection: 'column' as const, alignItems: 'center', color: 'warning.main',
};

const EMPTY_SX = {
  p: 2, mb: 0.75, borderStyle: 'dashed', textAlign: 'center' as const,
};

const STATUS_COLOR: Record<ShowWindowStatus, 'success' | 'info' | 'default'> = {
  live: 'success',
  scheduled: 'info',
  ended: 'default',
};

const formatWhen = (iso?: string | null) => (iso ? dayjs(iso).format('DD/MM HH:mm') : '');

const statusLabel = (card: CarouselCard, status: ShowWindowStatus) => {
  if (status === 'scheduled') { return __t('Agendado: entra em %{start}', { start: formatWhen(card.startAt) }); }
  if (status === 'ended') { return __t('Encerrado em %{end}', { end: formatWhen(card.endAt) }); }
  return card.endAt ? __t('No ar até %{end}', { end: formatWhen(card.endAt) }) : __t('No ar, sem data de fim');
};

const destinationLabel = (navigation?: HomeNavigation | null) => {
  if (!navigation?.destination) { return __t('Sem ação ao tocar'); }
  const { destination } = navigation;
  if (navigation.method === 'webview') { return __t('Abre o site %{url}', { url: destination }); }
  if (navigation.method === 'whatsapp') { return __t('Abre o WhatsApp %{phone}', { phone: destination }); }

  const choice = findNavigationChoice(navigationKeyOf(navigation));
  const screen = choice ? __t(choice.name) : destination;
  return navigationTargetIdOf(navigation)
    ? __t('Abre %{screen} (um item específico)', { screen })
    : __t('Abre %{screen}', { screen });
};

/**
 * Quem vê o card. Etiqueta vence grupo — mesma regra do backend ao publicar —, então
 * com as duas preenchidas só a etiqueta aparece aqui.
 */
const audienceLabel = (card: CarouselCard, tagNames: Map<string, string>, groupNames: Map<string, string>) => {
  const names = (ids: string[], byId: Map<string, string>) => ids.map(id => byId.get(id) || id).join(', ');
  if (card.tagIds?.length) { return __t('Só para: %{names}', { names: names(card.tagIds, tagNames) }); }
  if (card.groupIds?.length) { return __t('Só para os grupos: %{names}', { names: names(card.groupIds, groupNames) }); }
  return __t('Todos os clientes');
};

interface CardRowProps {
  id: string;
  position: number;
  card: CarouselCard;
  tagNames: Map<string, string>;
  groupNames: Map<string, string>;
  onEdit(): void;
  onRemove(): void;
}

const CardRow: React.FC<CardRowProps> = ({ id, position, card, tagNames, groupNames, onEdit, onRemove }) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id });
  const status = showWindowStatus(card);

  return (
    <Paper
      ref={setNodeRef}
      variant="outlined"
      sx={{
        display: 'flex', alignItems: 'center', gap: 1.5, p: 1, mb: 0.75,
        opacity: isDragging ? 0.4 : (status === 'ended' ? 0.6 : 1),
        transform: CSS.Transform.toString(transform),
        transition,
      }}
    >
      <Box {...attributes} {...listeners} sx={DRAG_HANDLE_SX}>
        <DragIndicatorIcon fontSize="small" />
      </Box>
      <Typography variant="caption" color="text.secondary" sx={{ width: 16, flexShrink: 0 }}>
        {position}
      </Typography>

      <Box sx={THUMB_SX}>
        {card.imagePath ? (
          <Box component="img" src={card.imagePath} alt="" sx={THUMB_IMG_SX} />
        ) : (
          <Box sx={MISSING_IMAGE_SX}>
            <WarningAmberIcon fontSize="small" />
            <Typography variant="caption">Sem imagem</Typography>
          </Box>
        )}
      </Box>

      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography variant="body2" noWrap>{destinationLabel(card.navigation)}</Typography>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 0.5 }}>
          <Chip size="small" variant="outlined" color={STATUS_COLOR[status]} label={statusLabel(card, status)} />
          <Chip size="small" variant="outlined" label={audienceLabel(card, tagNames, groupNames)} />
        </Box>
      </Box>

      <Tooltip title="Editar imagem">
        <IconButton size="small" onClick={onEdit}><EditIcon fontSize="small" /></IconButton>
      </Tooltip>
      <Tooltip title="Remover do carrossel">
        <IconButton size="small" onClick={onRemove}><DeleteIcon fontSize="small" /></IconButton>
      </Tooltip>
    </Paper>
  );
};

interface Editing { index: number | null; block: HomeBlock }

/**
 * Cards do carrossel da Home, no mesmo desenho do editor de seções logo acima: lista
 * arrastável e um diálogo por card. A imagem sobe na hora em que é escolhida, pela
 * mesma rota dos blocos — o PATCH de configurações leva só o caminho, sem `imagefile`.
 */
const CarouselCardsInput: React.FC<CarouselCardsInputProps> = ({ source = 'firebaseConfig.home_cards', chain }) => {
  const dataProvider = useDataProvider();
  const { field } = useInput({ source });
  const cards = useMemo(() => (field.value || []) as CarouselCard[], [field.value]);

  // Chave estável por card, só para o arrastar: o card não tem id, e gravar um seria
  // recusado pelo validator do backend.
  const keysRef = useRef<string[]>([]);
  if (keysRef.current.length !== cards.length) {
    keysRef.current = cards.map(() => newId());
  }
  const keys = keysRef.current;

  const [editing, setEditing] = useState<Editing | null>(null);
  const [tags, setTags] = useState<Named[]>([]);
  const [groups, setGroups] = useState<Named[]>([]);

  useEffect(() => {
    dataProvider.getList('chains/chainId/tag', {
      pagination: { page: 1, perPage: 200 },
      sort: { field: 'name', order: 'ASC' },
      filter: { type: TagType.client },
    })
      .then(({ data }) => setTags((data || []) as Named[]))
      .catch(() => setTags([]));
    dataProvider.getList('chains/chainId/customer-groups', {
      pagination: { page: 1, perPage: 200 },
      sort: { field: 'name', order: 'ASC' },
      filter: {},
    })
      .then(({ data }) => setGroups((data || []) as Named[]))
      .catch(() => setGroups([]));
  }, [dataProvider]);

  const tagNames = useMemo(() => new Map(tags.map(tag => [tag.id, tag.name])), [tags]);
  const groupNames = useMemo(() => new Map(groups.map(group => [group.id, group.name])), [groups]);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 4 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  const commit = (next: CarouselCard[], nextKeys: string[]) => {
    keysRef.current = nextKeys;
    field.onChange(next);
  };

  const handleDragEnd = ({ active, over }: DragEndEvent) => {
    if (!over || active.id === over.id) { return; }
    const from = keys.indexOf(String(active.id));
    const to = keys.indexOf(String(over.id));
    if (from < 0 || to < 0) { return; }
    commit(arrayMove(cards, from, to), arrayMove(keys, from, to));
  };

  const handleRemove = (index: number) => commit(
    cards.filter((_, position) => position !== index),
    keys.filter((_, position) => position !== index),
  );

  const handleSave = (saved: HomeBlock) => {
    if (!editing) { return; }
    const { id, ...card } = saved;
    if (editing.index === null) {
      commit([...cards, card], [...keys, id]);
    } else {
      commit(
        cards.map((current, position) => (position === editing.index ? { ...current, ...card } : current)),
        keys,
      );
    }
    setEditing(null);
  };

  return (
    <Box sx={{ width: '100%', maxWidth: 760 }}>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
        As imagens passam em sequência na seção Carrossel da Home, nesta ordem. Arraste para mudar.
      </Typography>

      {cards.length === 0 ? (
        <Paper variant="outlined" sx={EMPTY_SX}>
          <Typography variant="body2" color="text.secondary">
            Nenhuma imagem no carrossel. Sem imagem no ar, a seção não aparece no app.
          </Typography>
        </Paper>
      ) : (
        <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
          <SortableContext items={keys} strategy={verticalListSortingStrategy}>
            {cards.map((card, index) => (
              <CardRow
                key={keys[index]}
                id={keys[index]}
                position={index + 1}
                card={card}
                tagNames={tagNames}
                groupNames={groupNames}
                onEdit={() => setEditing({ index, block: { ...card, id: keys[index] } })}
                onRemove={() => handleRemove(index)}
              />
            ))}
          </SortableContext>
        </DndContext>
      )}

      <Button
        startIcon={<AddIcon />}
        onClick={() => setEditing({
          index: null,
          block: { id: newId(), imagePath: null, navigation: { method: 'screen', destination: '' }, tagIds: [] },
        })}
        sx={{ mt: 1 }}
      >
        Adicionar imagem
      </Button>

      <BannerFormDialog
        kind="carousel"
        block={editing?.block || null}
        layout="full"
        chain={chain}
        onClose={() => setEditing(null)}
        onSave={handleSave}
      />
    </Box>
  );
};

export default CarouselCardsInput;
